/**
 * Who still owes picks this round, for the Commissioner and Admins
 * (ISSUE-1310). One row per member with their progress and a Nudge button.
 * The list, the counts and whether a member was already nudged this round
 * are all server fields; one nudge per member per round is the server's cap.
 * Pages arrive by cursor, a page at a time.
 */
import {
  usePickemMissingPicksList,
  usePickemNudge,
} from "@sportsmet/contest-sdk/private";
import { Button, Card, InlineBanner, Skeleton } from "@sportsmet/ui";
import { type ReactNode, useState } from "react";

import { showApiError } from "../../errors/apiErrorBus";
import {
  PICKEM_MISSING_EMPTY,
  PICKEM_MISSING_FAILED,
  PICKEM_MISSING_HEADING,
  PICKEM_MISSING_LOADING,
  PICKEM_MISSING_MORE_LABEL,
  PICKEM_MISSING_NUDGE_LABEL,
  PICKEM_MISSING_NUDGED_LABEL,
  PICKEM_MISSING_NUDGING_LABEL,
  PICKEM_RETRY,
  pickemMissingCountLine,
  pickemNudgeLabel,
} from "./pickemCopy";

const PAGE_SIZE = 25;

const MissingPage = (props: {
  poolId: string;
  roundId: string;
  cursor: string | null;
  isFirst: boolean;
  isLast: boolean;
  pendingUserId: string | null;
  onNudge: (userId: string) => void;
  onMore: (cursor: string) => void;
}): ReactNode => {
  const query = usePickemMissingPicksList({
    poolId: props.poolId,
    roundId: props.roundId,
    cursor: props.cursor,
    limit: PAGE_SIZE,
  });
  const data = query.data?.data ?? null;

  if (query.isLoading) {
    return (
      <li>
        <Skeleton shape="card" label={PICKEM_MISSING_LOADING} />
      </li>
    );
  }
  if (query.isError || data === null) {
    return (
      <li data-testid="pickem-missing-failed">
        <InlineBanner
          variant="warning"
          message={PICKEM_MISSING_FAILED}
          action={{
            label: PICKEM_RETRY,
            onAction: () => {
              void query.refetch();
            },
          }}
        />
      </li>
    );
  }
  if (props.isFirst && data.items.length === 0) {
    return (
      <li
        className="text-body text-ink-secondary"
        data-testid="pickem-missing-empty"
      >
        {PICKEM_MISSING_EMPTY}
      </li>
    );
  }
  const nextCursor = data.nextCursor;
  return (
    <>
      {data.items.map((item) => (
        <li
          key={item.userId}
          className="flex items-center gap-3 py-1"
          data-testid="pickem-missing-row"
          data-user-id={item.userId}
        >
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="truncate text-body text-ink">
              {item.displayName}
            </span>
            <span className="text-caption text-ink-secondary tabular-nums">
              {pickemMissingCountLine({
                made: item.picksMade,
                required: item.picksRequired,
              })}
            </span>
          </span>
          {item.isNudged ? (
            <span
              className="text-caption text-ink-secondary"
              data-testid="pickem-missing-nudged"
            >
              {PICKEM_MISSING_NUDGED_LABEL}
            </span>
          ) : (
            <Button
              size="sm"
              variant="secondary"
              aria-label={pickemNudgeLabel(item.displayName)}
              loading={props.pendingUserId === item.userId}
              loadingLabel={PICKEM_MISSING_NUDGING_LABEL}
              data-testid={`pickem-nudge-${item.userId}`}
              onClick={() => {
                props.onNudge(item.userId);
              }}
            >
              {PICKEM_MISSING_NUDGE_LABEL}
            </Button>
          )}
        </li>
      ))}
      {props.isLast && nextCursor !== null ? (
        <li className="py-1">
          <Button
            variant="ghost"
            data-testid="pickem-missing-more"
            onClick={() => {
              props.onMore(nextCursor);
            }}
          >
            {PICKEM_MISSING_MORE_LABEL}
          </Button>
        </li>
      ) : null}
    </>
  );
};

export const PickemMissingPicksCard = (props: {
  poolId: string;
  roundId: string;
}): ReactNode => {
  const [cursors, setCursors] = useState<(string | null)[]>([null]);
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);
  const nudge = usePickemNudge();

  async function sendNudge(userId: string): Promise<void> {
    setPendingUserId(userId);
    try {
      const result = await nudge.mutateAsync({
        poolId: props.poolId,
        roundId: props.roundId,
        userId,
      });
      showApiError(result);
    } catch {
      // The shared mutation cache raised the offline modal already.
    } finally {
      setPendingUserId(null);
    }
  }

  return (
    <Card data-testid="pickem-missing-card">
      <h2 className="text-body-strong text-ink">{PICKEM_MISSING_HEADING}</h2>
      <ul className="flex flex-col">
        {cursors.map((cursor, index) => (
          <MissingPage
            key={cursor ?? "first"}
            poolId={props.poolId}
            roundId={props.roundId}
            cursor={cursor}
            isFirst={index === 0}
            isLast={index === cursors.length - 1}
            pendingUserId={pendingUserId}
            onNudge={(userId) => {
              void sendNudge(userId);
            }}
            onMore={(next) => {
              setCursors((previous) => [...previous, next]);
            }}
          />
        ))}
      </ul>
    </Card>
  );
};
